import { TYPE_COLORS } from './state';
import { FONT_BODY, FONT_MONO } from './theme';

/* ────────────────────────────────────────────────────────────────────────── */
/*  UI primitives                                                             */
/* ────────────────────────────────────────────────────────────────────────── */

export function Card({ t, children, style, padding = 20, ...rest }) {
  return (
    <div {...rest} style={{
      background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: t.cardRadius || 12,
      padding, transition: 'border-color 0.15s', ...style,
    }}>{children}</div>
  );
}

export function Button({ t, variant = 'default', size = 'md', children, style, disabled, ...rest }) {
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 12, borderRadius: 8 },
    md: { padding: '8px 14px', fontSize: 13, borderRadius: 10 },
    lg: { padding: '12px 20px', fontSize: 14, borderRadius: 12 },
  };
  const variants = {
    default: { background: t.surfaceAlt, color: t.text, border: `1px solid ${t.cardBorder}` },
    primary: { background: t.accent, color: '#ffffff', border: `1px solid ${t.accent}` },
    ghost: { background: 'transparent', color: t.textMuted, border: '1px solid transparent' },
    danger: { background: t.dangerSoft, color: t.danger, border: `1px solid ${t.dangerSoft}` },
  };
  return (
    <button {...rest} disabled={disabled} style={{
      ...sizes[size], ...variants[variant],
      fontFamily: FONT_BODY, fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex', alignItems: 'center', gap: 6,
      transition: 'all 0.15s', whiteSpace: 'nowrap',
      ...style,
    }}>{children}</button>
  );
}

export function Input({ t, style, ...rest }) {
  return (
    <input {...rest} style={{
      background: t.inputBg, color: t.text, fontFamily: FONT_BODY, fontSize: 13,
      border: `1px solid ${t.inputBorder}`, borderRadius: 8, padding: '8px 12px',
      outline: 'none', width: '100%', transition: 'border-color 0.15s',
      ...style,
    }} />
  );
}

export function NumberInput({ t, value, onChange, step = 1, min = 0, suffix, placeholder, style }) {
  return (
    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <input
        type="number" inputMode="decimal" step={step} min={min}
        value={value ?? ''}
        placeholder={placeholder != null && placeholder !== '' ? String(placeholder) : undefined}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={{
          background: t.inputBg, color: t.text, fontFamily: FONT_MONO, fontSize: 14, fontWeight: 600,
          border: `1px solid ${t.inputBorder}`, borderRadius: 8,
          padding: '8px 12px', paddingRight: suffix ? 36 : 12,
          outline: 'none', width: '100%', textAlign: 'center',
          ...style,
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, color: t.textFaint, fontFamily: FONT_BODY, pointerEvents: 'none',
        }}>{suffix}</span>
      )}
    </div>
  );
}

export function Toggle({ t, checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)} style={{
      width: 36, height: 20, borderRadius: 999, border: 'none', cursor: 'pointer',
      background: checked ? t.accent : t.inputBorder,
      position: 'relative', transition: 'background 0.18s',
      flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: checked ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.18s',
      }} />
    </button>
  );
}

export function StatTile({ t, label, value, hint, accent = false }) {
  return (
    <div style={{
      background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: t.cardRadius || 12,
      padding: '14px 16px',
    }}>
      <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontFamily: FONT_MONO, fontWeight: 700, color: accent ? t.accent : t.text, lineHeight: 1.1 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function TypeDot({ type }) {
  return <span style={{ width: 6, height: 6, borderRadius: '50%', background: TYPE_COLORS[type] || '#94a3b8', display: 'inline-block', flexShrink: 0 }} />;
}

export function SectionLabel({ t, children, right }) {
  return (
    <div style={{ padding: '12px 18px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{children}</span>
      {right}
    </div>
  );
}
